import { cn } from '@/app/lib/utils';
import CustomSelect, { Option, CustomSelectProps } from '../ui/CustomSelect';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { getUserList, getUser } from '@/api/user';
import { useMutation, useQuery } from '@tanstack/react-query';
import Avatar from '../ui/Avatar';
import Icon from '../ui/Icon';
import { User } from '@/types/user';

type Props = {
  showEmail?: boolean;
  defaultValue: any;
  label?: string;
  required?: boolean;
  wrapperClassName?: string;
  className?: string;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  placeholder?: string;
  multiple?: boolean;
  onOptionSelect: (option: any, user?: User) => void;
  fetchSelectedOptionOnMount?: boolean;
  filterData?: (data: User[]) => User[];
} & Omit<Partial<CustomSelectProps>, 'onOptionSelect'>;

const UserSelect = ({
  showEmail = true,
  defaultValue,
  label, // ... (keeping existing destructured props)
  required,
  wrapperClassName,
  className,
  searchable,
  clearable,
  disabled,
  placeholder,
  onOptionSelect,
  fetchSelectedOptionOnMount,
  multiple,
  filterData,
  ...props
}: Props) => {
  // ... (useState hooks)
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [isOptionCbTriggered, setIsOptionCbTriggered] = useState(false);
  const { clientId } = useParams<{ clientId: string }>();
  // ... (useQuery hooks)
  const { data: options, isLoading } = useQuery({
    queryKey: ['users', clientId, search],
    queryFn: () =>
      getUserList({
        clientId: clientId || '',
        status: 'active',
        filters: [{ field: 'user_type', operator: 'eq', value: 'external' }],
        search: search,
      }),
    enabled: !!clientId,
  });
  // ... (useMutation hook)
  const {
    mutate: getUserMutation,
    data: missingUser,
    isPending: isLoadingMissingUser,
    isError: isErrorMissingUser,
  } = useMutation({
    mutationFn: (id: string) => getUser(clientId || '', id),
    onError: error => {
      console.error('error', error);
    },
  });

  // ... (optionsList useMemo - keeping as is)
  const optionsList = useMemo(() => {
    const filteredData = filterData ? filterData(options?.data || []) : options?.data || [];
    const list: Option[] =
      filteredData?.map(option => ({
        id: option.id,
        value:
          (option.first_name + ' ' + option.last_name || '-') +
          (showEmail ? ' | ' + option.email : ''),
        prefix: (
          <div className="mr-2">
            <Avatar
              size={32}
              name={option.first_name + ' ' + option.last_name}
              src={option.profile_pic_url}
              isExternalUrl
              initialsClassName="text-xs"
            />
          </div>
        ),
      })) || [];
    if (!multiple && selectedOption && list.find(o => o.id === selectedOption.id) === undefined) {
      list.unshift({
        id: selectedOption.id.toString(),
        value: selectedOption.value.toString(),
        prefix: selectedOption.prefix,
      });
    }
    if (multiple && selectedOptions) {
      // find missing options
      const missingOptions = selectedOptions.filter(o => !list.some(c => c.id === o.id));
      list.unshift(...missingOptions);
    }
    if (missingUser) {
      list.unshift({
        id: missingUser.id,
        value: (missingUser.first_name || '') + ' ' + (missingUser.last_name || '') || '-',
        prefix: (
          <div className="mr-2">
            <Avatar
              size={32}
              name={(missingUser.first_name || '') + ' ' + (missingUser.last_name || '')}
              src={missingUser.profile_pic_url}
              isExternalUrl
              initialsClassName="text-xs"
            />
          </div>
        ),
      });
    }
    return list;
  }, [options, selectedOption, selectedOptions, multiple, missingUser, filterData]);

  useEffect(() => {
    if (fetchSelectedOptionOnMount && defaultValue && !isOptionCbTriggered && !isLoading) {
      let values: Option | Option[] | null = null;
      if (multiple) {
        values = optionsList.filter(o => defaultValue.includes(o.id)) || [];
      } else {
        values = optionsList.find(o => o.id === defaultValue) || null;
      }
      if ((Array.isArray(values) && values.length !== 0) || values !== null) {
        if (multiple) {
          onOptionSelect(values);
        } else {
          const val = values as Option;
          const foundUser = options?.data?.find(u => u.id === val.id);
          onOptionSelect(values, foundUser);
        }
        setIsOptionCbTriggered(true);
      } else if (defaultValue || (Array.isArray(defaultValue) && defaultValue.length !== 0)) {
        // call api
        if (!isErrorMissingUser) {
          getUserMutation(defaultValue);
        }
      }
    }
  }, [
    fetchSelectedOptionOnMount,
    defaultValue,
    optionsList,
    onOptionSelect,
    isOptionCbTriggered,
    isLoading,
    multiple,
    getUserMutation,
    isErrorMissingUser,
  ]);

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-sm font-medium mb-1.5 flex items-center gap-1 text-muted-foreground">
          <span>
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
          </span>
          {isLoadingMissingUser && (
            <Icon
              name="loader-2-fill"
              size={4}
              aria-label="Loading"
              className="animate-spin w-4 h-4"
            />
          )}
        </label>
      )}
      <CustomSelect
        options={optionsList || []}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onOptionSelect={option => {
          if (multiple) {
            setSelectedOptions(option as Option[]);
          } else {
            setSelectedOption(option as Option);
          }
          onOptionSelect(
            option,
            options?.data?.find(u => u.id === (Array.isArray(option) ? undefined : option?.id))
          );
        }}
        searchable={searchable}
        clearable={clearable}
        disabled={disabled}
        triggerClassNames={className}
        onSearch={search => {
          setSearch(search);
        }}
        searchLoading={isLoading}
        multiple={multiple}
        {...props}
      />
    </div>
  );
};

export default UserSelect;
